import {
	Card,
	CardBody,
	Input,
	Table,
	TableCell,
	TableContainer,
	TableHeader,
} from '@windmill/react-ui';
import DataWrapper from './DataWrapper';
import NotFound from 'components/table/NotFound';
import PageTitle from 'components/Typography/PageTitle';
import MainDrawer from 'components/drawer/MainDrawer';
import ApkUploadDrawer from 'components/drawer/ApkUploadDrawer';
import { SidebarContext } from 'context/SidebarContext';
import useAsync from 'hooks/useAsync';
import useToggleDrawer from 'hooks/useToggleDrawer';
import { useState, useEffect, useContext, useMemo } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import sidebar from 'routes/sidebar';
import ExportData from 'components/common/ExportData';
import ApkWaiterServices from 'services/ApkWaiterServices';
import ApkWaiterTable from 'components/apkWaiter/ApkWaiterTable';

// Custom debounce function
const debounce = (func, wait) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
};

const style = `
  .downloadbtn .svg-inline--fa {
    font-size: 30px; 
    color: grey;
    padding: 9px 10px;
    margin: unset;
  }
  .data-table-container::-webkit-scrollbar {
    width: 8px;
  }
  .data-table-container::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 4px;
  }
  .data-table-container::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  @media (max-width: 640px) {
    .data-table-container {
      overflow-x: auto;
    }
  }
`;

const ApkWaiter = () => {
	const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
	const { setIsUpdate } = useContext(SidebarContext);
	const { serviceId } = useToggleDrawer();
	const [searchText, setSearchText] = useState('');
	const [debouncedSearchText, setDebouncedSearchText] = useState('');
	const { data, loading, error } = useAsync(() =>
		ApkWaiterServices.getApkWaiterList()
	);
	const [status, setStatus] = useState('');

	// Debounce search input
	const handleSearch = debounce((value) => {
		setDebouncedSearchText(value);
	}, 300);

	useEffect(() => {
		handleSearch(searchText);
	}, [searchText]);

	// Ensure data is an array, handle null/undefined/promise cases
	const isDataArray = Array.isArray(data?.data);
	const safeData = isDataArray && data?.data ? data.data : [];

	const isAdmin = adminInfo?.id === 1;
	const hideView =
		isAdmin ||
		adminInfo?.permission?.some(
			(perm) => perm.id_tab.toLowerCase() === 'apk waiter' && perm.view === 1
		);
	const hideEdit =
		isAdmin ||
		adminInfo?.permission?.some(
			(perm) => perm.id_tab.toLowerCase() === 'apk waiter' && perm.edit === 1
		);
	const hideExport =
		isAdmin ||
		adminInfo?.permission?.some(
			(perm) => perm.id_tab.toLowerCase() === 'apk waiter' && perm.export === 1
		);
	const hideDelete =
		isAdmin ||
		adminInfo?.permission?.some(
			(perm) => perm.id_tab.toLowerCase() === 'apk waiter' && perm.delete === 1
		);

	useEffect(() => {
		setStatus(data?.status || false);
	}, [data?.status]);

	const searchTerms = debouncedSearchText.toLowerCase().split(' ');
	const filteredData = useMemo(
		() =>
			safeData.filter((item) =>
				searchTerms.every(
					(term) =>
						(item.name != null &&
							String(item.name).toLowerCase().includes(term)) ||
						(item.email != null &&
							String(item.email).toLowerCase().includes(term)) ||
						(item.mobile != null &&
							String(item.mobile).toLowerCase().includes(term)) ||
						(item.created_at != null &&
							String(item.Requested_At).toLowerCase().includes(term))
				)
			),
		[safeData, debouncedSearchText]
	);

	const csvData = [
		['Sl. No.', 'Name', 'Email', 'Mobile.no', 'Requested At'],
		...safeData.map(({ name, email, mobile, created_at }, index) => [
			index + 1,
			name || '',
			email || '',
			mobile || '',
			created_at || '',
		]),
	];

	const handleRefresh = () => {
		setIsUpdate(true);
	};

	return (
		<>
			{hideView && (
				<>
					<style>{style}</style>
					<PageTitle sidebar={sidebar().sidebar} />
					<MainDrawer>
						<ApkUploadDrawer id={serviceId} />
					</MainDrawer>
					<Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
						<CardBody>
							<form className="gap-6 flex items-center">
								<div className="flex-1">
									<Input
										className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white"
										type="search"
										name="search"
										value={searchText}
										autoComplete="off"
										onChange={(e) => setSearchText(e.target.value)}
										placeholder="Quick Search"
									/>
								</div>
								{hideExport && (
									<ExportData
										title="APK Waiter List"
										exportData={csvData}
										fileNames="APK Waiter List"
									/>
								)}
								<FiRefreshCcw
									onClick={handleRefresh}
									cursor="pointer"
									aria-label="Refresh data"
								/>
							</form>
						</CardBody>
					</Card>

					<DataWrapper loading={loading} error={error} status={status}>
						{error ? (
							<div className="text-red-500 text-center p-4">
								Failed to load data: {error.message || 'An error occurred'}
								<button
									onClick={handleRefresh}
									className="ml-4 px-4 py-2 bg-blue-500 text-white rounded"
								>
									Retry
								</button>
							</div>
						) : loading ? (
							<TableContainer>
								<Table>
									<TableHeader className="custom-table-header capitalize sticky top-0 z-10">
										<tr>
											<TableCell className="text-center">Sl.No.</TableCell>
											<TableCell className="text-center">Name</TableCell>
											<TableCell className="text-center">Email</TableCell>
											<TableCell className="text-center">Mobile.no</TableCell>
											<TableCell className="text-center">
												Requested At
											</TableCell>
											{!(hideEdit && hideDelete) && (
												<TableCell className="text-center">Actions</TableCell>
											)}
										</tr>
									</TableHeader>
									<tbody>
										{[...Array(10)].map((_, i) => (
											<tr key={`skeleton-${i}`}>
												<TableCell>
													<div className="h-4 bg-gray-200 rounded w-12 mx-auto"></div>
												</TableCell>
												<TableCell>
													<div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
												</TableCell>
												<TableCell>
													<div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
												</TableCell>
												<TableCell>
													<div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
												</TableCell>
												<TableCell>
													<div className="h-4 bg-gray-200 rounded w-40 mx-auto"></div>
												</TableCell>
												{!(hideEdit && hideDelete) && (
													<TableCell>
														<div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
													</TableCell>
												)}
											</tr>
										))}
									</tbody>
								</Table>
							</TableContainer>
						) : filteredData.length > 0 ? (
							<TableContainer
								className="mb-8"
								style={{
									maxHeight: '65vh',
									overflowY: 'scroll',
									scrollbarWidth: 'thin',
								}}
							>
								<Table>
									<TableHeader className="custom-table-header capitalize sticky top-0 z-10">
										<tr>
											<TableCell className="text-center">Sl.No.</TableCell>
											<TableCell className="text-center">Name</TableCell>
											<TableCell className="text-center">Email</TableCell>
											<TableCell className="text-center">Mobile.no</TableCell>
											<TableCell className="text-center">
												Requested At
											</TableCell>
											{!(hideEdit && hideDelete) && (
												<TableCell className="text-center">Actions</TableCell>
											)}
										</tr>
									</TableHeader>
									<ApkWaiterTable
										apkList={filteredData}
										hideEdit={hideEdit}
										hideDelete={hideDelete}
									/>
								</Table>
							</TableContainer>
						) : (
							<NotFound title="There are no APK waiters." />
						)}
					</DataWrapper>
				</>
			)}
		</>
	);
};

export default ApkWaiter;
